import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CircleCheck, Loader2, XCircle, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_TRIES = 15;

function usePaymentStatus(sessionId, provider, onPaid) {
  const [state, setState] = useState(sessionId ? "checking" : "error");
  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    const url = provider === "mp" ? `/payments/mercadopago/confirm/${sessionId}` : `/payments/status/${sessionId}`;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(url);
        if (data.payment_status === "paid") { setState(data.test ? "test_paid" : "paid"); onPaid(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) {
        if (e?.response?.status === 404) { setState("error"); return; }
        console.warn("payment status check failed", e);
      }
      if (tries >= MAX_TRIES) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, provider, onPaid]);
  return state;
}

const COPY = {
  checking: { icon: Loader2, spin: true, title: "Confirmando seu pagamento...", desc: "Isso leva só alguns segundos." },
  paid: { icon: CircleCheck, title: "Plano ativado!", desc: "Seu plano Profissional está liberado por 30 dias. O recibo foi enviado para seu e-mail." },
  test_paid: { icon: CircleCheck, title: "Pagamento de teste confirmado!", desc: "O Mercado Pago aprovou o pagamento de R$ 1,00 e o app recebeu a confirmação. Nenhum plano foi alterado. Você pode estornar pelo painel do Mercado Pago." },
  timeout: { icon: Loader2, title: "Pagamento em processamento", desc: "Se você pagou com Pix, a confirmação pode levar alguns minutos. Seu plano será ativado automaticamente." },
  error: { icon: XCircle, title: "Pagamento não concluído", desc: "Nada foi cobrado. Você pode tentar novamente em Configurações." },
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const provider = params.get("provider");
  const state = usePaymentStatus(provider === "mp" ? params.get("external_reference") : params.get("session_id"), provider, refresh);
  const c = COPY[state];
  return (
    <div className="fade-up mx-auto mt-10 max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center" data-testid="payment-success-page">
      <c.icon className={`mx-auto h-14 w-14 ${state === "error" ? "text-amber-500" : "text-emerald-600"} ${c.spin ? "animate-spin" : ""}`} />
      <h1 className="mt-4 font-display text-3xl font-extrabold text-slate-900" data-testid="payment-status-title">{c.title}</h1>
      <p className="mt-2 text-slate-500">{c.desc}</p>
      <Link to={state === "paid" ? "/nova-analise" : "/configuracoes"} data-testid="payment-continue-btn" className="ai-cta mt-6 inline-flex h-12 items-center gap-2 rounded-xl px-6 font-semibold text-white">
        {state === "paid" ? "Começar uma análise" : "Voltar para Configurações"} <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="fade-up mx-auto mt-10 max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center" data-testid="payment-cancel-page">
      <XCircle className="mx-auto h-14 w-14 text-amber-500" />
      <h1 className="mt-4 font-display text-3xl font-extrabold text-slate-900">Pagamento cancelado</h1>
      <p className="mt-2 text-slate-500">Nenhuma cobrança foi feita. Você pode assinar quando quiser.</p>
      <Link to="/configuracoes" data-testid="payment-back-btn" className="mt-6 inline-flex h-12 items-center rounded-xl bg-slate-900 px-6 font-semibold text-white">Ver planos</Link>
    </div>
  );
}
